import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `notification` of the console gateway — owned by plan-002 unit **RB-21** (design §15.6.1).
 *
 * The shell's notification centre. `listNotifications` and `readAllNotifications` are both
 * session-only; producers are out of scope, so an empty list is the TRUE state until the build,
 * billing-alert and conversation-flag writers land — the centre shows an empty list, never a
 * refusal and never a fixture.
 *
 * ── HOW THIS FILE WORKS (IC-2, created by RB-03) ─────────────────────────────────────────
 * The method signatures below are merged onto `ConsoleGateway` by declaration merging, so
 * RB-21 owns them here and never edits `gateway.ts`. `notificationGap` is this area's rows of
 * `GATEWAY_GAP`, which `gateway.ts` concatenates; it is EMPTY now that both routes exist
 * (RB-21 landed them). `notificationApiSlice` is what `apiGateway` spreads. The sandbox
 * (`../sandbox-gateway.ts`) is untouched.
 *
 * ── THE MAPPING (design §15.6.1) ─────────────────────────────────────────────────────────
 * `dek ← body`, `whenText ← formatWhen(created_at)`, `iconName ← icon_name`. The time is worded
 * here, from the fact, never by the API (§15.5.2). `kind` and `link_path` are decoded — a
 * malformed response raises — but the view does not carry them yet, because no screen renders
 * them.
 *
 * ── A MESSAGE A CALLER LEFT (plan-018 WAPP-B) ───────────────────────────────────────────────
 * The two derived kinds `taken_message.recorded` and `taken_message.person_requested` arrive worded in English (ADR
 * 0051); `../taken-message-view.ts` words their title and body in the person's language and points the link at the
 * conversation with History's "Messages" filter on. Every other kind is shown as the API wrote it.
 *
 * ── CALLER PROTECTION (plan-029 WAPP-HIST1) ───────────────────────────────────────────────
 * `party_protection.party_refused_often` and `party_access.internal_voice_agent_lock_pending` / `_locked` are worded
 * from the row's `detail` by `../../shell/party-notice.ts` (History with the "Refused" chip on, or the Channels tab).
 *
 * ── NO CURSOR ON THE METHOD, SO THE SLICE FOLLOWS `next_cursor` ─────────────────────────
 * `listNotifications()` takes no cursor, so design §15.5.3 applies: follow `next_cursor` until it
 * is `null`, and stop with an error after {@link PAGE_CEILING} pages rather than silently
 * truncating the list.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway, NotificationView } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";
import {
  isTakenMessageNoticeKind,
  takenMessageNoticeBodyOf,
  takenMessageNoticeLinkOf,
  takenMessageNoticeTitleOf,
} from "../taken-message-view.js";
import { isPartyNoticeKind, partyNoticeWordsOf, type PartyNoticeWords } from "../../shell/party-notice.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    listNotifications(): Promise<readonly NotificationView[]>;
    readAllNotifications(): Promise<void>;
  }
}

export const notificationGap: readonly GatewayGap[] = [];

/** Design §15.5.3: a cursorless console method follows at most this many pages, then raises. */
export const PAGE_CEILING = 20;

/** The page size the slice asks for: the API's maximum, so the ceiling is as far off as it can be. */
const PAGE_LIMIT = 100;

/** Raised when the list is longer than {@link PAGE_CEILING} pages. Never a silent truncation. */
export class NotificationListTooLongError extends Error {
  override readonly name = "NotificationListTooLongError";
  constructor() {
    super(
      portalText("shell", "listnotifications_followed_value_pages_of_value_and_the_ap_95f6201f", { v0: String(String(PAGE_CEILING)), v1: String(String(PAGE_LIMIT)) }),
    );
  }
}

function viewOf(wire: unknown, path: string, now: Date, context: SliceContext): NotificationView {
  const row = readRecord(wire, path);
  // Decoded for its shape; only a message a caller left is worded by kind (see the header).
  const kind = readText(row["kind"], `${path}.kind`);
  const takenMessage = isTakenMessageNoticeKind(kind) ? kind : null;
  const rawLinkPath = row["link_path"];
  const apiLinkPath = rawLinkPath === null ? null : readText(rawLinkPath, `${path}.link_path`);
  // plan-029 WAPP-HIST1: caller protection's notices are worded from their `detail` (`../../shell/party-notice.ts`).
  const partyNotice = isPartyNoticeKind(kind) ? kind : null;
  const partyWords = (): PartyNoticeWords | null =>
    partyNotice === null ? null : partyNoticeWordsOf(partyNotice, row["detail"], now);
  const linkPath = partyWords()?.linkPath ?? (takenMessage === null ? apiLinkPath : takenMessageNoticeLinkOf(apiLinkPath));
  return validatedDisplayView({
    notificationId: readText(row["notification_id"], `${path}.notification_id`),
    get title() {
      const title = readText(row["title"], `${path}.title`);
      const party = partyWords();
      if (party !== null) return party.title;
      return takenMessage === null ? title : takenMessageNoticeTitleOf(takenMessage, title);
    },
    get dek() {
      const body = readText(row["body"], `${path}.body`);
      const party = partyWords();
      if (party !== null) return party.dek;
      return takenMessage === null ? body : takenMessageNoticeBodyOf(body);
    },
    get whenText() { return context.formatWhen(readText(row["created_at"], `${path}.created_at`), now); },
    unread: readBoolean(row["unread"], `${path}.unread`),
    iconName: readText(row["icon_name"], `${path}.icon_name`),
    linkPath,
  });
}

export function notificationApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<ConsoleGateway, "listNotifications" | "readAllNotifications"> {
  return {
    async listNotifications(): Promise<readonly NotificationView[]> {
      const now = context.now();
      const view: NotificationView[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < PAGE_CEILING; page += 1) {
        const query: { limit: number; cursor?: string } =
          cursor === null ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor };
        const body = readRecord(
          assertOk(await client.listNotifications({ query })),
          "listNotifications",
        );
        for (const one of readArray(body["data"], "listNotifications.data")) {
          view.push(viewOf(one, `notification[${view.length}]`, now, context));
        }
        const next = body["next_cursor"];
        if (next === null) return view;
        cursor = readText(next, "listNotifications.next_cursor");
      }
      throw new NotificationListTooLongError();
    },

    async readAllNotifications(): Promise<void> {
      assertOk(await client.readAllNotifications({}));
    },
  };
}
